/**
 * DynamicFieldRenderer - Shared component for rendering product config fields.
 * 
 * Used across: JobCardPage, JobCardsPage (edit dialog), CreateCatalogItemPage,
 * EditCatalogItemPage, CreateOrderPage, and standalone job card creation.
 * 
 * Handles: text, number, select, checkbox_group, toggle, date, datetime, textarea
 * Features: conditional visibility, auto-fill tags, ply-dependent dynamic fields,
 *           empty state styling, consistent behavior across all surfaces.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

/**
 * Check if a field should be visible based on conditional logic.
 */
export function isFieldVisible(field, values) {
  if (!field.conditional) return true;
  const { field: depField, values: condValues, show_when } = field.conditional;
  const depVal = values[depField];

  if (show_when === 'in') {
    if (!depVal) return false;
    if (Array.isArray(depVal)) return depVal.some(v => condValues?.includes(v));
    return condValues?.includes(depVal);
  }
  if (show_when === 'not_in') {
    if (!depVal) return true;
    if (Array.isArray(depVal)) return !depVal.some(v => condValues?.includes(v));
    return !condValues?.includes(depVal);
  }
  if (show_when === 'not_empty') {
    return depVal !== undefined && depVal !== null && depVal !== '' && depVal !== 'None';
  }
  return true;
}

/**
 * Check if a ply-dependent field should be visible based on current ply value.
 */
export function isPlyFieldVisible(field, values) {
  if (!field.ply_dependent) return true;
  const plyValue = parseInt(values?.ply) || 0;
  const layer = field.ply_layer || 0;
  return layer <= plyValue;
}

/**
 * Filter fields to only visible ones, considering both conditional and ply logic.
 */
export function getVisibleFields(fields, values, opts = {}) {
  const { excludeNames = [], skipAutoFilled = false } = opts;
  return fields.filter(f => {
    if (excludeNames.includes(f.name)) return false;
    if (skipAutoFilled && f.auto_filled) return false;
    if (!isFieldVisible(f, values)) return false;
    if (!isPlyFieldVisible(f, values)) return false;
    return true;
  });
}

/**
 * Group fields by section.
 */
export function groupFieldsBySection(fields) {
  const groups = {};
  fields.forEach(f => {
    const section = f.section || 'Other';
    if (!groups[section]) groups[section] = [];
    groups[section].push(f);
  });
  return groups;
}

// the unit is typed into the box along with the numbers, so the label says what
// the numbers are rather than which unit they are in. a label is a flat shape on
// a web, so it is described by two dimensions where a carton takes three.
export function sizeSuffix(orderType) {
  return orderType === 'LBL' ? ' (WxH)' : ' (LxBxH)';
}

/**
 * Single field renderer component.
 */
export function DynamicField({
  field,
  value,
  onChange,
  autoFilled = false,
  compact = false,
  showEmptyState = false,
  // the dense spec grid supplies its own label cell, so the control renders bare
  hideLabel = false,
  orderType,
}) {
  const isEmpty = value === undefined || value === null || value === '' ||
    (typeof value === 'string' && value.toLowerCase() === 'none');
  const cleanLabel = field.label?.replace(/\s*\([^)]*\)\s*$/, '') || field.name;
  const unitSuffix = field.name === 'size' ? sizeSuffix(orderType) : '';

  return (
    <div className={hideLabel ? 'w-full' : 'space-y-1.5'}>
      {!hideLabel && (
        <div className="flex items-center gap-1.5">
          <Label className={`${compact ? 'text-xs' : 'text-xs'} font-medium text-slate-600`}>
            {cleanLabel}{unitSuffix}
            {field.required && <span className="text-red-400 ml-0.5">*</span>}
          </Label>
          {autoFilled && !isEmpty && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">
              from order
            </span>
          )}
        </div>
      )}

      {field.type === 'select' ? (
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger className={`h-9 text-sm ${showEmptyState && isEmpty ? 'text-slate-400 border-dashed' : ''}`}>
            <SelectValue placeholder={showEmptyState && isEmpty ? 'Not yet filled' : 'Select...'} />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map(o => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : field.type === 'checkbox_group' ? (
        <div className={`flex flex-wrap gap-2 p-2 rounded-lg border ${showEmptyState && isEmpty ? 'border-dashed border-slate-200' : 'border-slate-200'}`}>
          {showEmptyState && isEmpty && <span className="text-xs text-slate-400 italic">Not yet filled</span>}
          {(field.options || []).filter(o => o !== 'None').map(option => {
            const vals = Array.isArray(value) ? value : [];
            const checked = vals.includes(option);
            return (
              <label key={option} className="flex items-center gap-1.5 text-xs cursor-pointer">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) => {
                    const current = Array.isArray(value) ? [...value] : [];
                    if (c) {
                      onChange([...current.filter(v => v !== 'None'), option]);
                    } else {
                      const next = current.filter(v => v !== option);
                      onChange(next.length ? next : []);
                    }
                  }}
                />
                <span className={checked ? 'text-slate-800 font-medium' : 'text-slate-500'}>{option}</span>
              </label>
            );
          })}
        </div>
      ) : field.type === 'toggle' ? (
        <div className="flex items-center gap-2">
          <Switch
            checked={value === 'Yes' || value === true}
            onCheckedChange={c => onChange(c ? 'Yes' : 'No')}
          />
          <span className={`text-sm ${isEmpty ? 'text-slate-400' : 'text-slate-700'}`}>
            {value === 'Yes' ? 'Yes' : value === 'No' ? 'No' : 'Not set'}
          </span>
        </div>
      ) : field.type === 'textarea' ? (
        <Textarea
          className={`text-sm min-h-[80px] ${showEmptyState && isEmpty ? 'border-dashed placeholder:text-slate-400' : ''}`}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={showEmptyState ? 'Not yet filled' : field.label}
        />
      ) : field.type === 'number' ? (
        <Input
          type="number"
          className={`h-9 text-sm ${showEmptyState && isEmpty ? 'border-dashed placeholder:text-slate-400' : ''}`}
          value={value ?? ''}
          onChange={e => onChange(e.target.value ? Number(e.target.value) : '')}
          placeholder={showEmptyState ? 'Not yet filled' : ''}
        />
      ) : field.type === 'date' ? (
        <Input
          type="date"
          className="h-9 text-sm"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
        />
      ) : field.type === 'datetime' ? (
        <Input
          type="datetime-local"
          className="h-9 text-sm"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
        />
      ) : (
        <Input
          type="text"
          className={`h-9 text-sm ${showEmptyState && isEmpty ? 'border-dashed placeholder:text-slate-400' : ''}`}
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={showEmptyState ? 'Not yet filled' : ''}
        />
      )}
    </div>
  );
}

/**
 * Ply change confirmation dialog.
 */
export function PlyChangeDialog({ open, onConfirm, onCancel, oldPly, newPly }) {
  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reduce Ply Count?</AlertDialogTitle>
          <AlertDialogDescription>
            Reducing ply count from {oldPly} to {newPly} will clear data in the extra layer fields (Layer {parseInt(newPly) + 1} through Layer {oldPly}). Continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Continue</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Custom hook for ply change handling with confirmation.
 */
export function usePlyChangeHandler(values, setValues) {
  const [plyDialog, setPlyDialog] = useState({ open: false, oldPly: '', newPly: '' });

  const handlePlyChange = useCallback((newPly) => {
    const currentPly = parseInt(values?.ply) || 0;
    const newPlyNum = parseInt(newPly) || 0;

    // If reducing ply, check if there's data to lose
    if (newPlyNum < currentPly) {
      let hasDataInExtraLayers = false;
      for (let i = newPlyNum + 1; i <= currentPly; i++) {
        if (values[`layer_${i}_paper_board`]) {
          hasDataInExtraLayers = true;
          break;
        }
      }

      if (hasDataInExtraLayers) {
        setPlyDialog({ open: true, oldPly: String(currentPly), newPly: String(newPlyNum) });
        return; // Don't change yet
      }
    }

    // Safe to change
    setValues(prev => ({ ...prev, ply: newPly }));
  }, [values, setValues]);

  const confirmPlyChange = useCallback(() => {
    const newPlyNum = parseInt(plyDialog.newPly) || 0;
    const oldPlyNum = parseInt(plyDialog.oldPly) || 0;

    // Clear extra layer fields
    const updates = { ply: plyDialog.newPly };
    for (let i = newPlyNum + 1; i <= oldPlyNum; i++) {
      updates[`layer_${i}_paper_board`] = '';
    }
    setValues(prev => ({ ...prev, ...updates }));
    setPlyDialog({ open: false, oldPly: '', newPly: '' });
  }, [plyDialog, setValues]);

  const cancelPlyChange = useCallback(() => {
    setPlyDialog({ open: false, oldPly: '', newPly: '' });
  }, []);

  return { plyDialog, handlePlyChange, confirmPlyChange, cancelPlyChange };
}

/**
 * Renders a complete section of fields with proper grouping.
 */
export function DynamicFieldSection({
  fields,
  values,
  onChange,
  autoFilledFields = [],
  compact = false,
  showEmptyState = false,
  columns = 2,
  sectionTitle,
  sectionClassName = '',
  onPlyChange, // Special handler for ply field
  orderType,
}) {
  const visibleFields = getVisibleFields(fields, values);

  if (visibleFields.length === 0) return null;

  return (
    <div className={sectionClassName}>
      {sectionTitle && (
        <h4 className="font-semibold text-sm text-slate-700 mb-3">{sectionTitle}</h4>
      )}
      <div className={`grid grid-cols-1 ${columns === 2 ? 'md:grid-cols-2' : columns === 3 ? 'md:grid-cols-3' : ''} gap-3`}>
        {visibleFields.map(field => (
          <DynamicField
            key={field.name}
            field={field}
            value={values[field.name]}
            onChange={(val) => {
              // Special handling for ply field
              if (field.name === 'ply' && onPlyChange) {
                onPlyChange(val);
              } else {
                onChange(field.name, val);
              }
            }}
            autoFilled={autoFilledFields.includes(field.name)}
            compact={compact}
            showEmptyState={showEmptyState}
            orderType={orderType}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Renders all fields grouped by section.
 */
export function DynamicFieldForm({
  fields,
  values,
  onChange,
  autoFilledFields = [],
  compact = false,
  showEmptyState = false,
  columns = 2,
  onPlyChange,
  excludeNames = [],
  orderType,
}) {
  const visibleFields = getVisibleFields(fields, values, { excludeNames });
  const sections = groupFieldsBySection(visibleFields);

  return (
    <>
      {Object.entries(sections).map(([sectionName, sectionFields], idx) => (
        <DynamicFieldSection
          key={sectionName}
          fields={sectionFields}
          values={values}
          onChange={onChange}
          autoFilledFields={autoFilledFields}
          compact={compact}
          showEmptyState={showEmptyState}
          columns={columns}
          sectionTitle={sectionName}
          sectionClassName={idx > 0 ? 'border-t pt-4 mt-4' : ''}
          onPlyChange={onPlyChange}
          orderType={orderType}
        />
      ))}
    </>
  );
}

export default DynamicField;
